/**
 * 2026-04-25: 제련소 / 각인소 진입 시 장비를 먼저 고르는 오버레이.
 *
 * 기존 동선: 인벤토리 → 아이템 → "제련 열기" 버튼 → RefineModal
 * 추가 동선: 제련소(메뉴) → 장비 목록 (필터링) → 클릭 → RefineModal
 *
 * 두 동선 모두 같은 RefineModal 을 띄워 일관성 유지.
 */
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { tokens } from "@/art/tokens";
import { equipmentIcon } from "@/components/forge/equipmentIcon";
import { resolveEquipmentAuraColor } from "@/components/forge/EquipmentAura";
import { RefineModal } from "@/components/RefineModal";
import { EngraveCeremonyModal } from "@/components/EngraveCeremonyModal";
import { ENGRAVE_META } from "@/utils/skillLabels";
import { useRun } from "@/state/run";
import { getCharacterSheet } from "@/api/endpoints/inventory";
import type { InventoryItemView } from "@/api/types";

interface Props {
    runId: string;
    mode: "refine" | "engrave";
    onClose: () => void;
}

export function ForgePickerOverlay({ runId, mode, onClose }: Props) {
    const run = useRun(s => s.run);
    const inventory = useRun(s => s.inventory);
    const setSheet = useRun(s => s.setSheet);
    const setInventory = useRun(s => s.setInventory);
    const currentGold = run?.gold;
    const [picked, setPicked] = useState<InventoryItemView | null>(null);
    const [version, setVersion] = useState(0);  // 모달 종료 후 인벤토리 갱신 트리거

    // 진입 시 최신 sheet/inventory 를 한 번 갱신
    useEffect(() => {
        let alive = true;
        getCharacterSheet(runId)
            .then(res => {
                if (!alive) return;
                setSheet(res);
                setInventory(res.inventory);
            })
            .catch(() => {});
        return () => { alive = false; };
    }, [runId, setSheet, setInventory, version]);

    const inventoryItems = inventory?.items ?? [];

    // 모드별 필터링
    const candidates = useMemo(() => {
        return inventoryItems.filter(inv => {
            const cat = inv.definition?.category;
            if (mode === "refine") {
                // 무기·방어구·장신구 + 만렙 미달
                if (cat !== "WEAPON" && cat !== "ARMOR" && cat !== "ACCESSORY") return false;
                if ((inv.enhancement ?? 0) >= 10) return false;
                return true;
            } else {
                // 각인은 무기만
                if (cat !== "WEAPON") return false;
                return true;
            }
        });
    }, [inventoryItems, mode]);

    // 2026-04-25: 장소별 아이덴티티 — 색·아이콘·NPC kicker 로 "여긴 다른 곳" 임을 보여줌.
    const place = mode === "refine"
        ? {
            title: "제련소",
            kicker: "대장간",
            placeIcon: "🔨",
            accentColor: tokens.color.brass,
            ambient: "달궈진 쇠 냄새 · 모루 위 망치질",
            emptyTitle: "두드릴 것이 없네",
            emptyBody: "모든 장비가 만렙(+10)에 닿았거나, 아직 손에 쥔 장비가 없어.",
        }
        : {
            title: "각인 의식",
            kicker: "도장",
            placeIcon: "✦",
            accentColor: tokens.color.moss,
            ambient: "촛농 · 룬 분필 · 침묵의 무게",
            emptyTitle: "이름을 새길 무기가 없어",
            emptyBody: "각인은 무기에만 가능합니다. 무기를 손에 넣고 다시 오세요.",
        };

    const onModalClose = () => {
        setPicked(null);
        setVersion(v => v + 1);  // 인벤토리 새로 부르기
    };

    return (
        <div style={overlayStyle} onClick={onClose}>
            <motion.div
                initial={{ opacity: 0, scale: 0.97, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                style={{
                    ...panelStyle,
                    // 장소별 accent border + glow
                    borderColor: `${place.accentColor}66`,
                    boxShadow: `0 24px 80px rgba(0,0,0,0.6), 0 0 60px ${place.accentColor}1a, inset 0 1px 0 ${place.accentColor}1a`,
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* 상단 ambient 라인 — 장소의 색을 거의 안 보이는 빛으로 깐다 */}
                <div aria-hidden style={{
                    position: "absolute", top: 0, left: 0, right: 0, height: 3,
                    background: `linear-gradient(90deg, transparent, ${place.accentColor}aa, transparent)`,
                    opacity: 0.85,
                }} />

                <div style={headerStyle}>
                    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                        {/* 장소 큰 아이콘 — 단순 emoji 가 NPC 노릇 */}
                        <div style={{
                            width: 60, height: 60, borderRadius: "50%",
                            background: `radial-gradient(circle, ${place.accentColor}33, ${tokens.color.ink}cc)`,
                            border: `1px solid ${place.accentColor}88`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 32,
                            boxShadow: `0 0 18px ${place.accentColor}33`,
                        }}>
                            {place.placeIcon}
                        </div>
                        <div>
                            <div style={{ ...kickerStyle, color: place.accentColor, opacity: 0.85 }}>
                                {place.kicker}
                            </div>
                            <h2 style={titleStyle}>{place.title}</h2>
                            <div style={{
                                fontSize: 11, color: tokens.color.parchmentDim, opacity: 0.55,
                                letterSpacing: 2, marginTop: 2, fontStyle: "italic",
                            }}>
                                {place.ambient}
                            </div>
                        </div>
                    </div>
                    <button style={closeButtonStyle} onClick={onClose} aria-label="닫기">✕</button>
                </div>

                {candidates.length === 0 ? (
                    <div style={emptyStateStyle}>
                        <div style={{
                            fontSize: 42, opacity: 0.4, marginBottom: 14,
                            color: place.accentColor,
                        }}>
                            {place.placeIcon}
                        </div>
                        <div style={{ fontSize: 16, color: tokens.color.parchment, marginBottom: 6, letterSpacing: 1 }}>
                            {place.emptyTitle}
                        </div>
                        <div style={{ fontSize: 13, opacity: 0.62, lineHeight: 1.6 }}>
                            {place.emptyBody}
                        </div>
                    </div>
                ) : (
                    <div style={gridStyle}>
                        {candidates.map(inv => (
                            <ItemCard
                                key={inv.instanceId}
                                item={inv}
                                mode={mode}
                                onClick={() => setPicked(inv)}
                            />
                        ))}
                    </div>
                )}
            </motion.div>

            {picked && mode === "refine" && (
                <RefineModal
                    runId={runId}
                    item={picked}
                    inventoryItems={inventoryItems}
                    onClose={onModalClose}
                    onComplete={() => { /* sheet 갱신은 onModalClose 에서 처리 */ }}
                />
            )}
            {picked && mode === "engrave" && (
                <EngraveCeremonyModal
                    runId={runId}
                    item={picked}
                    inventoryItems={inventoryItems}
                    currentGold={currentGold}
                    onClose={onModalClose}
                    onComplete={() => {}}
                />
            )}
        </div>
    );
}

function ItemCard({ item, mode, onClick }: {
    item: InventoryItemView;
    mode: "refine" | "engrave";
    onClick: () => void;
}) {
    const icon = equipmentIcon(item);
    const auraColor = resolveEquipmentAuraColor(item.enhancement, item.engraveTier);
    const name = item.definition?.name ?? "장비";
    const enhancement = item.enhancement ?? 0;
    const engraveLabel = item.engraveId
        ? ENGRAVE_META[item.engraveId as keyof typeof ENGRAVE_META]?.label ?? item.engraveId
        : null;
    const isMaxed = enhancement >= 10;

    return (
        <motion.button
            type="button"
            onClick={onClick}
            whileHover={{ y: -2 }}
            transition={{ duration: 0.15 }}
            style={{
                ...cardStyle,
                borderColor: auraColor ? `${auraColor}88` : `${tokens.color.brass}44`,
                boxShadow: auraColor
                    ? `0 0 22px ${auraColor}33, inset 0 0 14px ${auraColor}1f`
                    : `0 6px 18px rgba(0,0,0,0.4)`,
            }}
        >
            <div style={iconBadgeStyle}>
                <span style={{ fontSize: 38 }}>{icon}</span>
                <div style={{
                    ...enhancementBadgeStyle,
                    color: isMaxed ? tokens.color.brass : tokens.color.parchment,
                    borderColor: isMaxed ? tokens.color.brass : `${tokens.color.brass}66`,
                }}>
                    +{enhancement}
                </div>
            </div>
            <div style={{ marginTop: 10, textAlign: "center" }}>
                <div style={{ fontSize: 14, color: tokens.color.parchment, letterSpacing: 1 }}>
                    {name}
                </div>
                {mode === "engrave" && (
                    <div style={{
                        fontSize: 11, opacity: 0.7, marginTop: 3,
                        color: engraveLabel ? tokens.color.moss : tokens.color.parchmentDim,
                    }}>
                        {engraveLabel ? `✦ ${engraveLabel}` : "비어 있음"}
                    </div>
                )}
                {mode === "engrave" && (item.engraveCracks ?? 0) > 0 && (
                    <div style={{ fontSize: 10, color: tokens.color.burgundy, marginTop: 2 }}>
                        균열 {item.engraveCracks}
                    </div>
                )}
            </div>
        </motion.button>
    );
}

// ==================== styles ====================

const overlayStyle: React.CSSProperties = {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.78)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 990,
    padding: 24,
};

const panelStyle: React.CSSProperties = {
    width: "min(960px, 96vw)",
    maxHeight: "92vh",
    overflowY: "auto",
    background: tokens.color.ink,
    border: `1px solid ${tokens.color.brass}55`,
    borderRadius: 16,
    padding: "28px 36px 32px",
    boxShadow: `0 24px 80px rgba(0,0,0,0.6), 0 0 32px ${tokens.color.brass}1a`,
    color: tokens.color.parchment,
};

const headerStyle: React.CSSProperties = {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    marginBottom: 22,
};

const kickerStyle: React.CSSProperties = {
    fontSize: 12, color: tokens.color.parchmentDim, letterSpacing: 3,
};

const titleStyle: React.CSSProperties = {
    fontSize: 26, color: tokens.color.parchment, letterSpacing: 4,
    marginTop: 4, fontWeight: 500,
};

const closeButtonStyle: React.CSSProperties = {
    width: 36, height: 36, borderRadius: 8,
    background: "transparent",
    border: `1px solid ${tokens.color.brass}33`,
    color: tokens.color.parchment, opacity: 0.7, cursor: "pointer", fontSize: 14,
};

const gridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: 14,
};

const cardStyle: React.CSSProperties = {
    padding: "16px 12px 14px",
    borderRadius: 12,
    background: "rgba(255,255,255,0.025)",
    border: `1px solid ${tokens.color.brass}44`,
    cursor: "pointer",
    color: tokens.color.parchment,
    transition: "border-color 0.18s ease, box-shadow 0.18s ease",
    display: "flex", flexDirection: "column", alignItems: "center",
};

const iconBadgeStyle: React.CSSProperties = {
    position: "relative",
    width: 76, height: 76, borderRadius: "50%",
    background: "radial-gradient(circle, rgba(255,255,255,0.04), rgba(0,0,0,0.4))",
    border: `1px solid ${tokens.color.brass}55`,
    display: "flex", alignItems: "center", justifyContent: "center",
};

const enhancementBadgeStyle: React.CSSProperties = {
    position: "absolute", bottom: -4, right: -6,
    minWidth: 36, padding: "3px 8px",
    borderRadius: 14,
    background: tokens.color.ink,
    border: `1px solid ${tokens.color.brass}66`,
    fontSize: 12, fontWeight: 700, letterSpacing: 1,
    textAlign: "center",
};

const emptyStateStyle: React.CSSProperties = {
    padding: "60px 20px", textAlign: "center",
    color: tokens.color.parchmentDim, fontSize: 14, letterSpacing: 2,
    lineHeight: 1.7,
};
